import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Trophy, XCircle } from 'lucide-react';
import '../styles/PageLayout.css';

// Interfaces
interface Sport {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  createdAt: string;
  leaguesCount?: number;
  clubsCount?: number;
  usersCount?: number;
}

interface CreateSportRequest {
  name: string;
  description?: string;
  icon?: string;
}

interface UpdateSportRequest {
  name: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
}

const SportsManagement: React.FC = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSport, setEditingSport] = useState<Sport | null>(null);
  const [formData, setFormData] = useState<CreateSportRequest>({
    name: '',
    description: '',
    icon: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // API Base URL - use environment variable or current origin
  const configuredBase = (import.meta as any).env?.VITE_API_BASE as string | undefined;
  const API_BASE = configuredBase && /^https?:\/\//i.test(configuredBase)
    ? `${configuredBase}/api`
    : `${window.location.origin}/api`;

  // Load sports data
  const loadSports = async () => {
    try {
      setLoading(true);
      const url = `${API_BASE}/sports?includeInactive=true`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSports(data);
      setError(null);
    } catch (err) {
      console.error('Error loading sports:', err);
      setError('Fehler beim Laden der Sportarten');
    } finally {
      setLoading(false);
    }
  };

  // Create new sport
  const createSport = async (sportData: CreateSportRequest) => {
    try {
      const response = await fetch(`${API_BASE}/sports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sportData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Fehler beim Erstellen der Sportart');
      }

      await loadSports();
      resetForm();
      setShowForm(false);
    } catch (err) {
      console.error('Error creating sport:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Erstellen der Sportart');
    }
  };

  // Update sport
  const updateSport = async (id: number, sportData: UpdateSportRequest) => {
    try {
      const response = await fetch(`${API_BASE}/sports/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sportData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Fehler beim Aktualisieren der Sportart');
      }

      await loadSports();
      resetForm();
      setEditingSport(null);
      setShowForm(false);
    } catch (err) {
      console.error('Error updating sport:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Aktualisieren der Sportart');
    }
  };

  // Delete sport
  const deleteSport = async (id: number) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Sportart löschen möchten?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/sports/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Fehler beim Löschen der Sportart');
      }

      await loadSports();
    } catch (err) {
      console.error('Error deleting sport:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen der Sportart');
    }
  };

  // Form handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingSport) {
      await updateSport(editingSport.id, {
        ...formData,
        isActive: editingSport.isActive
      });
    } else {
      await createSport(formData);
    }
  };

  const handleEdit = (sport: Sport) => {
    setEditingSport(sport);
    setFormData({
      name: sport.name,
      description: sport.description || '',
      icon: sport.icon || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: ''
    });
    setEditingSport(null);
  };

  const handleCancel = () => {
    resetForm();
    setShowForm(false);
  };

  // Toggle active status
  const toggleActiveStatus = async (sport: Sport) => {
    await updateSport(sport.id, {
      name: sport.name,
      description: sport.description,
      icon: sport.icon,
      isActive: !sport.isActive
    });
  };

  // Filter sports
  const filteredSports = sports.filter(sport => {
    const matchesSearch = sport.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sport.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActiveFilter = showInactive || sport.isActive;
    return matchesSearch && matchesActiveFilter;
  });

  // Load data on component mount
  useEffect(() => {
    loadSports();
  }, []);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="page-container"><div className="page-loading"><div className="page-spinner" /><p>Lade Sportarten...</p></div></div>
    );
  }

  return (
    <div className="page-container">
      {error && (
        <div className="page-error-banner">
          <span><XCircle size={14} /> {error}</span>
          <button onClick={() => setError(null)} className="page-toggle-btn" style={{ padding: '2px 8px', marginLeft: 'auto' }}>×</button>
        </div>
      )}

      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setShowForm(true)}>
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Inaktive anzeigen
          </label>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredSports.length} / {sports.length} Sportarten</span>
        </div>
        {/* Row 2: Search */}
        <div className="page-controls-row">
          <input className="page-search-input" type="text" placeholder="Sportarten durchsuchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {showForm && (
        <div className="page-modal-overlay">
          <div className="page-modal-content">
            <h3>{editingSport ? 'Sportart bearbeiten' : 'Neue Sportart erstellen'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="page-form-field">
                <label>Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="z.B. Fußball" />
              </div>
              <div className="page-form-field">
                <label>Beschreibung</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Beschreibung der Sportart..." rows={3} />
              </div>
              <div className="page-form-field">
                <label>Icon (Emoji)</label>
                <input type="text" value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} placeholder="Icon" maxLength={10} />
              </div>
              <div className="page-modal-actions">
                <button type="button" className="page-toggle-btn" onClick={handleCancel}>Abbrechen</button>
                <button type="submit" className="page-toggle-btn page-btn-create">{editingSport ? 'Aktualisieren' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <table className="page-table">
        <thead>
          <tr>
            <th>Icon</th>
            <th>Name</th>
            <th>Beschreibung</th>
            <th>Status</th>
            <th>Erstellt</th>
            <th>Verknüpfungen</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {filteredSports.map((sport) => (
            <tr key={sport.id}>
              <td><span style={{ fontSize: '1.5rem' }}>{sport.icon || '–'}</span></td>
              <td><strong>{sport.name}</strong></td>
              <td className="page-table-meta">{sport.description || '-'}</td>
              <td>
                <button className="page-badge" onClick={() => toggleActiveStatus(sport)} style={{ backgroundColor: sport.isActive ? '#28a745' : '#6c757d', color: '#fff', cursor: 'pointer', border: 'none' }}>
                  {sport.isActive ? 'Aktiv' : 'Inaktiv'}
                </button>
              </td>
              <td className="page-table-meta">{formatDate(sport.createdAt)}</td>
              <td className="page-table-meta">
                Ligen: {sport.leaguesCount || 0} · Vereine: {sport.clubsCount || 0} · Nutzer: {sport.usersCount || 0}
              </td>
              <td>
                <div className="page-table-actions">
                  <button className="page-toggle-btn" onClick={() => handleEdit(sport)} title="Bearbeiten"><Pencil size={14} /></button>
                  <button className="page-toggle-btn page-btn-delete" onClick={() => deleteSport(sport.id)} title="Löschen"><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filteredSports.length === 0 && (
        <div className="page-empty-state">
          <h3>Keine Sportarten gefunden</h3>
          <p>{searchTerm ? 'Keine Sportarten entsprechen Ihren Suchkriterien.' : 'Noch keine Sportarten vorhanden. Erstellen Sie die erste Sportart.'}</p>
        </div>
      )}
    </div>
  );
};

export default SportsManagement;
